import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getSupabase } from "@/lib/supabase";

/**
 * Rota intermediária do "Ver como aluno" (preview do trainer, no Kyra Desktop).
 *
 * O Worker (`trainer-students` → preview-magic-link) assina um JWT HS256 que
 * mimetiza o access_token do aluno e aponta o iframe pra:
 *   /s/:slug/preview-bridge?next=/s/:slug?preview=1#access_token=…&refresh_token=preview_…
 *
 * Aqui (SPA) aplicamos a sessão via supabase-js (`setSession` → localStorage,
 * não cookies SSR como o legado) e redirecionamos pro portal real já logado.
 * Como é a mesma origem dentro do iframe, o AppShell lê a sessão do localStorage
 * e renderiza o portal do aluno.
 */
export function PreviewBridge() {
  const { slug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const nextRaw = searchParams.get("next") || `/s/${slug}`;

    // Os tokens vêm no hash (#) — só existem no browser, nunca no servidor.
    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : "";
    if (!hash) {
      setError("Faltam credenciais de visualização no link.");
      return;
    }

    const sp = new URLSearchParams(hash);
    const access_token = sp.get("access_token");
    const refresh_token = sp.get("refresh_token");
    if (!access_token || !refresh_token) {
      setError("Tokens de visualização incompletos.");
      return;
    }

    let cancelled = false;
    (async () => {
      const { error: setErr } = await getSupabase().auth.setSession({
        access_token,
        refresh_token,
      });
      if (cancelled) return;
      if (setErr) {
        setError(`Não consegui aplicar a sessão: ${setErr.message}`);
        return;
      }
      // Limpa o hash pra não reusar tokens num reload acidental.
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      navigate(toRelativePath(nextRaw, slug), { replace: true });
    })();

    return () => {
      cancelled = true;
    };
  }, [slug, searchParams, navigate]);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-6 text-center">
      {error ? (
        <div className="flex flex-col items-center gap-2 animate-fade-in">
          <p className="text-sm font-medium text-danger">Não consegui abrir a visualização.</p>
          <p className="max-w-xs text-xs text-muted">{error}</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-accent-soft border-t-transparent" />
          <p className="text-sm text-muted">Preparando visualização…</p>
        </div>
      )}
    </div>
  );
}

/** Aceita path relativo OU URL absoluta; sempre devolve pathname+search pro router. */
function toRelativePath(next: string, slug: string): string {
  try {
    if (/^https?:\/\//i.test(next)) {
      const u = new URL(next);
      return u.pathname + u.search;
    }
    return next.startsWith("/") ? next : `/s/${slug}`;
  } catch {
    return `/s/${slug}`;
  }
}
